import {createBrowserHistory} from 'history';

import * as log from '../util/log';
import {isFgEvent, FgEventType, NewBgEvent, BgEventType} from '../bg/events';
import {confirmDialog} from '../util/confirm-dialog';

/**
 * Enforce only one instance of this running.
 */
let _singletonLock = false;

/**
 * URI of service worker.
 */
const SW_PATH = '/sw.js';

/**
 * Wrapper for interacting with the service worker from the main thread.
 *
 * This should be used as a singleton. It can be instantiated anywhere when the
 * app loads.
 */
export class NudgeServiceWorker {
  /**
   * Flag for tracking attachment state to UI.
   */
  private detached = false;

  /**
   * Flag for whether the service worker has been initialized.
   */
  private bgInit = false;

  /**
   * Flag for whether a service worker update is available.
   */
  private needsUpdate = false;

  /**
   * How often to check for service worker updates (milliseconds).
   */
  private updatePollingInterval = 5 * 60 * 1000;

  /**
   * Flag to track if update dialog is open.
   */
  private updateDialogOpen = false;

  /**
   * Load the service worker.
   *
   * If a service worker has already been called, this fails.
   */
  constructor() {
    if (_singletonLock) {
      throw new Error("Service worker can't be instantiated twice!");
    }

    // Some environments, like mobile and incognito, don't support service
    // workers. We'll need a workaround for those; not all features of the app
    // will be supported.
    if (!navigator.serviceWorker) {
      console.warn('Service worker not supported in this environment!');
      return;
    }

    // Prevent any other instance from being created.
    _singletonLock = true;

    navigator.serviceWorker.register(SW_PATH);
    this.installSysHandler();
    this.detached = false;

    // Poll for updates periodically
    setInterval(async () => {
      const reg = await this.ready();
      if (!reg) {
        return;
      }

      await reg.update();
      this.needsUpdate = !!reg.waiting;

      if (this.needsUpdate) {
        this.update();
      }
    }, this.updatePollingInterval);
  }

  public async waitForActivate(timeout: number = 5_000): Promise<void> {
    if (this.bgInit) {
      return;
    }

    const reg = await navigator.serviceWorker.ready;
    if (reg?.active) {
      this.bgInit = true;
      return;
    }

    return new Promise<void>((resolve) => {
      const t0 = Date.now();
      const check = () => {
        if (this.bgInit) {
          resolve();
          return;
        }
        if (Date.now() - t0 > timeout) {
          throw new Error('Service worker did not initialize in time');
        }
        setTimeout(check, 100);
      };
      check();
    });
  }

  /**
   * Force this and all other Nudge web app tabs to reload when necessary in
   * order to apply a service worker update.
   */
  public async update() {
    const reg = await this.ready();
    if (!reg) {
      return;
    }

    this.needsUpdate = false;
    const waiting = reg.waiting;
    if (!waiting) {
      log.warn(
        'Service worker was marked as ready for update but did not have any waiting registration!',
      );
      return;
    }

    if (this.updateDialogOpen) {
      return;
    }

    this.updateDialogOpen = true;

    await confirmDialog.confirm({
      title: 'Update available',
      description:
        'Nudge has been updated! This page needs to be reloaded, or else certain features might not work properly.',
      confirmText: 'Reload',
      cancelText: 'Cancel',
      onConfirm: () => {
        waiting.postMessage(NewBgEvent(BgEventType.Update, {}));
        window.location.reload();
      },
    });

    this.updateDialogOpen = false;
  }

  /**
   * Get the active/ready service worker registration.
   */
  public async ready() {
    if (!navigator.serviceWorker) {
      return undefined;
    }

    const reg = await navigator.serviceWorker.ready;

    if (!reg?.active) {
      throw new Error('Failed to find active service worker handle!');
    }

    // If the service worker is not controlled, it means that the page was
    // loaded before the service worker was registered, and it is not yet
    // controlling the page. This often happens after a hard refresh.
    // In this case, force a page reload to ensure the service worker will
    // control the page. If we don't, the page won't be able to authenticate
    // properly with the backend server.
    if (!navigator.serviceWorker.controller) {
      log.warn('Detected service worker without a controller, reloading page.');
      window.location.reload();
      return;
    }

    return reg;
  }

  /**
   * Request logout and cleanup of the service worker.
   */
  public async logout() {
    const reg = await this.ready();
    if (!reg) {
      console.error(
        "Service worker registration wasn't available, did not log out.",
      );
      return;
    }
    reg.active!.postMessage(NewBgEvent(BgEventType.Logout, {}));
    this.detached = true;
    return;
  }

  /**
   * Update the access token in the service worker's memory.
   */
  public async updateAccessToken(token: string) {
    const reg = await this.ready();
    if (!reg) {
      console.error(
        "Service worker registration wasn't available, did not update access token.",
      );
      return;
    }
    reg.active!.postMessage(NewBgEvent(BgEventType.AccessToken, {token}));
    return;
  }

  /**
   * Install a message handler for system events.
   *
   * This is intended to be called just once.
   */
  private installSysHandler() {
    if (!navigator.serviceWorker) {
      throw new Error('Service worker not supported in this environment!');
    }

    navigator.serviceWorker.addEventListener('message', (e) => {
      const data = e.data;
      if (!data) {
        return;
      }

      if (!isFgEvent(data)) {
        return;
      }

      switch (data.message) {
        case FgEventType.SysInit:
          log.debug('Received SysInit event from service worker');
          this.detached = false;
          this.bgInit = true;
          return;
        case FgEventType.SysLogout:
          log.debug('Received logout request from service worker');
          // Only force a logout if this UI is not already logged out,
          // otherwise there would be an infinite loop.
          if (!this.detached) {
            window.location.pathname = '/logout';
          }
          return;
        default:
          // Ignore any other message.
          return;
      }
    });

    // TODO -- this is the ideal way to apply changes, but it doesn't work
    // because react-router-dom has no way of accessing the history object.
    // Apply service worker updates automatically when navigating.
    const history = createBrowserHistory({window});
    history.listen(() => {
      this.update();
    });
  }
}
